import { useCallback, useEffect, useRef } from 'react';

import { useOktaAuth } from '@okta/okta-react';
import type { AxiosError, AxiosInstance } from 'axios';
import { useAtomValue } from 'jotai';

import {
  jwtReactQueryAxiosInstance,
  oktaReactQueryAxiosInstance,
} from '@/react-query/api';
import routesConstants from '@/routes/routes.constants';

import { useHandleLogout } from '../../app/appHeader/utils';
import { isMagicLinkUserAtom } from '../../modules/login/atoms';
import { endpoints } from '../../react-query/endpoints';
import {
  getExternalJwtToken,
  jwtAxiosInstace,
  oktaAxiosInstance,
} from '../../utils/api';
import { mixpanelEvents, useTrackEvent } from '../../utils/mixpanel';

function normalizeUrl(url?: string): string {
  if (!url) return '';
  const parsed = new URL(url);

  // Normalize the path — replace IDs or hashes with placeholders
  const normalizedPath = parsed.pathname
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      // Mongo ObjectId or UUID-like patterns
      if (/^[0-9a-f]{24}$/i.test(segment)) return ':id';
      if (
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
          segment,
        )
      )
        return ':id';
      if (/^\d+$/.test(segment)) return ':id'; // numeric IDs
      return segment;
    })
    .join('/');

  return `/${normalizedPath}`;
}

const WithAxios = ({ children }: { children: JSX.Element }) => {
  const { logout } = useHandleLogout();
  const { oktaAuth } = useOktaAuth();
  const { trackEvent } = useTrackEvent();

  const isMagicLinkUser = useAtomValue(isMagicLinkUserAtom);

  // Keep track of interceptor IDs so we can remove them when re-setting up
  const interceptorRefs = useRef<{
    oktaAxios: { request: number | null; response: number | null };
    oktaReactQuery: { request: number | null; response: number | null };
  }>({
    oktaAxios: { request: null, response: null },
    oktaReactQuery: { request: null, response: null },
  });

  const addPublicInterceptors = useCallback((instance: AxiosInstance) => {
    // Public routes only use JWT token from localStorage
    instance.interceptors.request.use(async (config) => {
      const externalJwtToken = getExternalJwtToken();
      if (externalJwtToken && externalJwtToken.trim() !== '') {
        config.headers.Authorization = `Bearer ${externalJwtToken}`;
      }

      // all patient-downloadable routes should be prefixed with /external/patient-downloadable
      if (
        config.url &&
        !config.url.includes(
          `${endpoints.external}/${endpoints.patientDownloadable}`,
        )
      ) {
        const url = new URL(config.url);
        const pathSegments = url.pathname.split('/').filter(Boolean);

        // Check if the first segment is an API version (like v2, v3, etc.)
        const hasApiVersion =
          pathSegments.length > 0 && /^v\d+$/.test(pathSegments[0]);

        if (hasApiVersion) {
          // Insert /external/patient-downloadable after the API version
          const apiVersion = pathSegments[0];
          const remainingPath = pathSegments.slice(1).join('/');
          url.pathname = `/${apiVersion}/${endpoints.external}/${endpoints.patientDownloadable}/${remainingPath}`;
        } else {
          // No API version, prepend as before
          url.pathname = `/${endpoints.external}/${endpoints.patientDownloadable}${url.pathname}`;
        }

        config.url = url.toString();
      }

      return config;
    });

    instance.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        // For public routes, we don't handle logout on auth errors
        // Just throw the error as-is
        throw error;
      },
    );
  }, []);

  // need a different interceptor for react query because it uses relative paths (for eg /fmx-summaries instead of http://localhost:8080/fmx-summaries)
  const addPublicInterceptorsForReactQuery = useCallback(
    (instance: AxiosInstance) => {
      // Public routes only use JWT token from localStorage
      instance.interceptors.request.use(async (config) => {
        const externalJwtToken = getExternalJwtToken();
        if (externalJwtToken && externalJwtToken.trim() !== '') {
          config.headers.Authorization = `Bearer ${externalJwtToken}`;
        }

        // Handle relative paths for React Query instances
        // all patient-downloadable routes should be prefixed with /external/patient-downloadable
        if (
          config.url &&
          !config.url.includes(
            `${endpoints.external}/${endpoints.patientDownloadable}`,
          )
        ) {
          // Ensure the path starts with / and add the prefix
          const path = config.url.startsWith('/')
            ? config.url
            : `/${config.url}`;

          const pathSegments = path.split('/').filter(Boolean);

          // Check if the first segment is an API version (like v2, v3, etc.)
          const hasApiVersion =
            pathSegments.length > 0 && /^v\d+$/.test(pathSegments[0]);

          if (hasApiVersion) {
            // Insert /external/patient-downloadable after the API version
            const apiVersion = pathSegments[0];
            const remainingPath = pathSegments.slice(1).join('/');
            config.url = `/${apiVersion}/${endpoints.external}/${endpoints.patientDownloadable}/${remainingPath}`;
          } else {
            // No API version, prepend as before
            config.url = `/${endpoints.external}/${endpoints.patientDownloadable}${path}`;
          }
        }

        return config;
      });

      instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          // For public routes, we don't handle logout on auth errors
          // Just throw the error as-is
          throw error;
        },
      );
    },
    [],
  );

  const addAuthenticatedInterceptors = useCallback(
    (instance: AxiosInstance, isReactQuery = false) => {
      // Define the current interceptor to avoid repeated conditions
      const interceptor =
        interceptorRefs.current[isReactQuery ? 'oktaReactQuery' : 'oktaAxios'];

      // Remove existing interceptors if they exist
      if (interceptor.request !== null) {
        instance.interceptors.request.eject(interceptor.request);
      }
      if (interceptor.response !== null) {
        instance.interceptors.response.eject(interceptor.response);
      }

      // Authenticated routes use Okta tokens
      const requestInterceptorId = instance.interceptors.request.use(
        async (config) => {
          // Always get the fresh token for each request
          const freshToken = await oktaAuth.getOrRenewAccessToken();
          if (freshToken) {
            config.headers.Authorization = `Bearer ${freshToken}`;
          }
          return config;
        },
      );

      const responseInterceptorId = instance.interceptors.response.use(
        (response) => response,
        async (error: AxiosError) => {
          const externalJwtToken = localStorage.getItem('external-jwt-token');
          const oktaTokenStorage = localStorage.getItem('okta-token-storage');
          const oktaTokenStorageParsed = oktaTokenStorage
            ? JSON.parse(oktaTokenStorage)
            : null;
          const accessToken = await oktaAuth.tokenManager
            .getTokens()
            .then((token) => token?.accessToken);
          const tokenExpiry = accessToken
            ? new Date(accessToken.expiresAt * 1000).toISOString()
            : null;
          const isTokenExpired =
            !!tokenExpiry && new Date(tokenExpiry) < new Date();

          switch (error?.response?.status) {
            case 403:
              if (error.request.responseURL.includes(endpoints.events)) {
                break;
              }
              if (isMagicLinkUser) {
                trackEvent(
                  mixpanelEvents.magicLinkRedirectToLogin,
                  {
                    cause: '403 logout',
                    responseUrl: error.request.responseURL,
                  },
                  undefined,
                  undefined,
                  false,
                );
              }

              await logout(undefined, {
                logoutType: 'FORCED',
                responseUrl: error.request?.responseURL,
                responseUrlPathNormalized: normalizeUrl(
                  error.request?.responseURL,
                ),
                httpStatus: error.response?.status,
                httpMethod: error.config?.method,
                requestHeaders: error.config?.headers,
                responseHeaders: error.response?.headers,
                responseData: error.response?.data,
                currentUrl: window.location.href,
                page: Object.entries(routesConstants).find(([, path]) =>
                  location.hash.includes(path),
                )?.[0],
                oktaSessionExists: await oktaAuth.session.exists(),
                oktaAccessToken: accessToken?.accessToken,
                oktaTokenStorage:
                  oktaTokenStorageParsed?.accessToken?.accessToken,
                externalJwtToken,
                isTokenExpired,
                tokenExpiry,
                isAuthenticated: await oktaAuth.isAuthenticated(),
              });
              break;
            default:
              break;
          }
          throw error;
        },
      );

      // Store the interceptor IDs for future cleanup
      interceptor.request = requestInterceptorId;
      interceptor.response = responseInterceptorId;
    },
    [isMagicLinkUser, logout, oktaAuth, trackEvent],
  );

  addPublicInterceptors(jwtAxiosInstace);
  addPublicInterceptorsForReactQuery(jwtReactQueryAxiosInstance);

  addAuthenticatedInterceptors(oktaAxiosInstance, false);
  addAuthenticatedInterceptors(oktaReactQueryAxiosInstance, true);

  // Listen for token renewal events and re-setup authenticated interceptors
  useEffect(() => {
    if (!oktaAuth?.tokenManager) return;

    const handleTokenRenewed = async () => {
      const tokens = await oktaAuth.tokenManager.getTokens();
      const accessToken = tokens?.accessToken;
      const tokenExpiry = accessToken
        ? new Date(accessToken.expiresAt * 1000).toISOString()
        : null;
      const timeUntilExpiry = tokenExpiry
        ? Math.round((new Date(tokenExpiry).getTime() - Date.now()) / 1000 / 60)
        : null;

      trackEvent(mixpanelEvents.oktaAuthTokenRenewed, {
        tokenExpiry,
        timeUntilExpiry,
      });

      // Re-setup authenticated interceptors when tokens are renewed to ensure fresh token usage
      addAuthenticatedInterceptors(oktaAxiosInstance, false);
      addAuthenticatedInterceptors(oktaReactQueryAxiosInstance, true);
    };

    const handleTokenExpired = async () => {
      const tokens = await oktaAuth.tokenManager.getTokens();

      trackEvent(mixpanelEvents.oktaAuthTokenExpired, {
        hasAccessToken: !!tokens?.accessToken,
        hasIdToken: !!tokens?.idToken,
      });

      // Re-setup authenticated interceptors when tokens expire to handle the next renewal
      addAuthenticatedInterceptors(oktaAxiosInstance, false);
      addAuthenticatedInterceptors(oktaReactQueryAxiosInstance, true);
    };

    // Listen for token renewal events
    oktaAuth.tokenManager.on('renewed', handleTokenRenewed);
    oktaAuth.tokenManager.on('expired', handleTokenExpired);

    // Cleanup event listeners
    return () => {
      oktaAuth.tokenManager.off('renewed', handleTokenRenewed);
      oktaAuth.tokenManager.off('expired', handleTokenExpired);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oktaAuth?.tokenManager, addAuthenticatedInterceptors]);

  return children;
};

export default WithAxios;
